/**
 * Cadastro do signatário dentro do wizard.
 *
 * CPF é opcional no formulário, mas o aviso é explícito: sem CPF a evidência de
 * autoria fica bem mais fraca se o contrato for contestado, e o certificado
 * digital nem pode ser conferido.
 */

import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import ProfessionalAutocomplete, {
  type ProfessionalOption,
} from '../ProfessionalAutocomplete';
import { SIGNER_ROLE_LABELS } from '../../constants/signature';
import type { SignerRole } from '../../types/signature';

export interface SignerDraft {
  name: string;
  cpf?: string;
  email?: string;
  phone?: string;
  birth_date?: string;
  sign_role: SignerRole;
  patient_id?: string | null;
  professional_id?: string | null;
}

interface SignerFormDialogProps {
  open: boolean;
  initial?: SignerDraft | null;
  requirePhone?: boolean;
  onClose: () => void;
  onSave: (signer: SignerDraft) => void;
}

const EMPTY: SignerDraft = { name: '', sign_role: 'patient' };

function maskCpf(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
}

function maskPhone(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
}

export default function SignerFormDialog({
  open,
  initial,
  requirePhone = false,
  onClose,
  onSave,
}: SignerFormDialogProps) {
  const [draft, setDraft] = useState<SignerDraft>(EMPTY);
  const [professional, setProfessional] = useState<ProfessionalOption | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDraft(initial ? { ...initial } : EMPTY);
      setProfessional(
        initial?.professional_id && initial?.name
          ? {
              id: initial.professional_id,
              full_name: initial.name,
            }
          : null,
      );
      setError(null);
    }
  }, [open, initial]);

  const set = <K extends keyof SignerDraft>(key: K, value: SignerDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    if (draft.name.trim().length < 3) {
      setError('Informe o nome completo do signatário.');
      return;
    }
    const phoneDigits = (draft.phone || '').replace(/\D/g, '');
    if (requirePhone && phoneDigits.length < 10) {
      setError('O envio por WhatsApp precisa de um número com DDD.');
      return;
    }
    if (draft.sign_role === 'professional' && !draft.professional_id) {
      setError('Selecione o profissional que irá assinar.');
      return;
    }
    onSave({
      ...draft,
      name: draft.name.trim(),
      cpf: draft.cpf?.replace(/\D/g, '') || undefined,
      phone: phoneDigits || undefined,
      email: draft.email?.trim() || undefined,
      professional_id: draft.professional_id || null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 17 }}>
        {initial ? 'Editar signatário' : 'Adicionar signatário'}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Nome completo"
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
            fullWidth
            autoFocus
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="CPF"
              value={maskCpf(draft.cpf || '')}
              onChange={(e) => set('cpf', e.target.value)}
              fullWidth
              placeholder="000.000.000-00"
              helperText="Fortalece a prova de autoria"
            />
            <TextField
              label="Data de nascimento"
              type="date"
              value={draft.birth_date || ''}
              onChange={(e) => set('birth_date', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={requirePhone ? 'WhatsApp' : 'WhatsApp (opcional)'}
              value={maskPhone(draft.phone || '')}
              onChange={(e) => set('phone', e.target.value)}
              fullWidth
              placeholder="(11) 99999-9999"
            />
            <TextField
              label="E-mail (opcional)"
              type="email"
              value={draft.email || ''}
              onChange={(e) => set('email', e.target.value)}
              fullWidth
            />
          </Stack>

          <TextField
            select
            label="Assina como"
            value={draft.sign_role}
            onChange={(e) => {
              const role = e.target.value as SignerRole;
              setDraft((prev) => ({
                ...prev,
                sign_role: role,
                professional_id: role === 'professional' ? prev.professional_id : null,
              }));
              if (role !== 'professional') setProfessional(null);
            }}
            fullWidth
          >
            {Object.entries(SIGNER_ROLE_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>

          {draft.sign_role === 'professional' && (
            <ProfessionalAutocomplete
              value={professional}
              onChange={(next) => {
                setProfessional(next);
                setDraft((prev) => ({
                  ...prev,
                  professional_id: next?.id || null,
                  name: next?.full_name || prev.name,
                }));
              }}
              label="Profissional signatário"
              required
              helperText="O nome será preenchido automaticamente a partir do cadastro."
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained" sx={{ textTransform: 'none' }}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
